import { useEffect, useRef } from 'react';
import { GeoJSONSource, LineLayer } from 'mapbox-gl';

import { color } from '@strava/ui-tokens/js';
import { LatLng, LngLat } from './types';
import { LAYER_ID_TO_DRAW_ON } from './constants';
import { useMap } from './useMap';
import { useMapStyle } from './useMapStyle';
import { latLngToLngLat, getBeforeIdToUse } from './utils';

type PolylineStyle = {
  lineColor?: string;
  lineWidth?: number;
  outlineColor?: string;
  outlineWidth?: number;
  opacity?: number;
};

export type PolylineProps = {
  /**
   * Unique ID of the layer.
   */
  id: string;

  /**
   * Coordinates of polyline.
   */
  latLng: LatLng[];

  /**
   * Polyline style and how it looks.
   */
  style?: PolylineStyle;

  /**
   * The ID of an existing layer to insert the new layer before, resulting in the new
   * layer appearing visually beneath the existing layer.
   */
  beforeId?: string;
};

export const polylineDefaultStyle: PolylineStyle = {
  lineColor: color.colorExtendedRedR2,
  lineWidth: 3,
  outlineColor: color.colorCoreWhite,
  outlineWidth: 5,
  opacity: 1
};

const Polyline = ({
  id,
  latLng,
  style: customStyle,
  beforeId = LAYER_ID_TO_DRAW_ON
}: PolylineProps) => {
  const map = useMap();
  const mapStyle = useMapStyle();
  const isMountedRef = useRef(true);

  const style = {
    ...polylineDefaultStyle,
    ...customStyle
  };
  const sourceName = `${id}-source`;

  const layer: LineLayer = {
    id,
    type: 'line',
    source: sourceName,
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-width': style.lineWidth,
      'line-color': style.lineColor,
      'line-color-transition': {
        duration: 0
      },
      'line-opacity': style.opacity
    }
  };

  const layerOutline: LineLayer = {
    ...layer,
    id: `${id}-outline`,
    source: sourceName,
    paint: {
      'line-width': style.outlineWidth,
      'line-color': style.outlineColor,
      'line-color-transition': {
        duration: 0
      },
      'line-opacity': style.opacity
    }
  };

  const addLayer = () => {
    if (map && !map.getSource(sourceName)) {
      map.addSource(sourceName, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      const beforeIdToUse = getBeforeIdToUse(map, beforeId);
      map.addLayer(layerOutline, beforeIdToUse).addLayer(layer, beforeIdToUse);
    }
  };

  const setData = () => {
    if (map?.getSource(sourceName)) {
      (map.getSource(sourceName) as GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: latLngToLngLat(latLng) as LngLat[]
            },
            properties: {
              id
            }
          }
        ]
      });

      const beforeIdToUse = getBeforeIdToUse(map, beforeId);
      map
        .moveLayer(layerOutline.id, beforeIdToUse)
        .moveLayer(layer.id, beforeIdToUse);
    }
  };

  useEffect(() => {
    if (!isMountedRef.current && map?.getLayer(layer.id)) {
      const beforeIdToUse = getBeforeIdToUse(map, beforeId);
      map
        .moveLayer(layerOutline.id, beforeIdToUse)
        .moveLayer(layer.id, beforeIdToUse);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeId]);

  useEffect(() => {
    if (!isMountedRef.current) {
      addLayer();
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  useEffect(() => {
    if (!isMountedRef.current) {
      setData();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latLng]);

  useEffect(() => {
    if (!isMountedRef.current) {
      map?.setPaintProperty(id, 'line-color', style.lineColor);
      map?.setPaintProperty(id, 'line-width', style.lineWidth);
      map?.setPaintProperty(id, 'line-opacity', style.opacity);
      map?.setPaintProperty(`${id}-outline`, 'line-color', style.outlineColor);
      map?.setPaintProperty(`${id}-outline`, 'line-width', style.outlineWidth);
      map?.setPaintProperty(`${id}-outline`, 'line-opacity', style.opacity);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    style.lineColor,
    style.lineWidth,
    style.opacity,
    style.outlineColor,
    style.outlineWidth
  ]);

  useEffect(() => {
    addLayer();
    setData();

    isMountedRef.current = false;

    return () => {
      // The Map interface does not have a style property, but the Mapbox GL JS map
      // object does.
      // @ts-expect-error
      if (map && Boolean(map.style) && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
        map.removeLayer(layerOutline.id);
        map.removeSource(sourceName);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default Polyline;
